import axios from 'axios';
import Constants from '../../assests/Constants/constants.js';
import DocumentService from '../../Services/documents.service';

export const TRANSFER_DOC_TYPES = {
    Incomes: 'Incomes',
    Expenses: 'Expenses',
    PaymentAcceptances: 'PaymentAcceptances',
    OtherDocuments: 'OtherDocuments',
    SignatureDocuments: 'SignatureDocuments',
    Forms: 'Forms',
    JournalEntries: 'JournalEntries',
    DocumentsRequiringClarification: 'DocumentsRequiringClarification',
};

/** Map backend English clarification move/restore errors to i18n keys. */
export const translateClarificationError = (t, errorText, fallbackKey = 'SendToClarificationFailed') => {
    const raw = (errorText || '').trim();
    if (!raw) return t(fallbackKey);
    const map = [
        ['Cannot move a locked document.', 'CannotMoveLockedDocument'],
        ['Cannot move a canceled document.', 'CannotMoveCanceledDocument'],
        ['Cannot move a canceled/voided document.', 'CannotMoveCanceledDocument'],
        ['Cannot move a matched document. Clear matches first.', 'CannotMoveMatchedDocument'],
        ['Cannot move a deposited journal entry.', 'CannotMoveDepositedJournal'],
        ['Cannot move a payment acceptance with deposited lines.', 'CannotMoveDepositedPaymentAcceptance'],
        ['Cannot move this document while related payment acceptances still reference it.', 'CannotMoveRelatedPaymentAcceptances'],
        ['Failed to delete source income while moving to clarification.', 'FailedDeleteSourceOnClarification'],
        ['Failed to delete source expense while moving to clarification.', 'FailedDeleteSourceOnClarification'],
        ['Failed to delete source journal entry while moving to clarification.', 'FailedDeleteSourceOnClarification'],
        ['Income not found.', 'DocumentNotFound'],
        ['Expense not found.', 'DocumentNotFound'],
        ['Journal entry not found.', 'DocumentNotFound'],
        ['Payment acceptance not found.', 'DocumentNotFound'],
        ['Clarification document not found.', 'ClarificationDocumentNotFound'],
        ['Clarification document is already resolved.', 'AlreadyResolvedClarification'],
        ['Invalid clarification snapshot.', 'InvalidClarificationSnapshot'],
        ['The reporting month is closed or not available.', 'ReportingMonthClosed'],
    ];
    const hit = map.find(([en]) => raw.includes(en));
    return hit ? t(hit[1]) : raw;
};

/**
 * Same transfer matrix as the single-document DocumentTransferDialog.
 */
export const getAllowedTransferTypes = (fromType) => {
    switch (fromType) {
        case TRANSFER_DOC_TYPES.Incomes:
            return [
                TRANSFER_DOC_TYPES.Expenses,
                TRANSFER_DOC_TYPES.PaymentAcceptances,
                TRANSFER_DOC_TYPES.OtherDocuments,
                TRANSFER_DOC_TYPES.DocumentsRequiringClarification,
            ];
        case TRANSFER_DOC_TYPES.Expenses:
            return [
                TRANSFER_DOC_TYPES.Incomes,
                TRANSFER_DOC_TYPES.PaymentAcceptances,
                TRANSFER_DOC_TYPES.OtherDocuments,
                TRANSFER_DOC_TYPES.DocumentsRequiringClarification,
            ];
        case TRANSFER_DOC_TYPES.PaymentAcceptances:
            return [
                TRANSFER_DOC_TYPES.Incomes,
                TRANSFER_DOC_TYPES.Expenses,
                TRANSFER_DOC_TYPES.OtherDocuments,
                TRANSFER_DOC_TYPES.DocumentsRequiringClarification,
            ];
        case TRANSFER_DOC_TYPES.JournalEntries:
            return [
                TRANSFER_DOC_TYPES.DocumentsRequiringClarification,
            ];
        case TRANSFER_DOC_TYPES.OtherDocuments:
            return [
                TRANSFER_DOC_TYPES.Incomes,
                TRANSFER_DOC_TYPES.Expenses,
                TRANSFER_DOC_TYPES.PaymentAcceptances,
            ];
        case TRANSFER_DOC_TYPES.SignatureDocuments:
        case TRANSFER_DOC_TYPES.Forms:
            return [TRANSFER_DOC_TYPES.OtherDocuments];
        case TRANSFER_DOC_TYPES.DocumentsRequiringClarification:
            // Restore is handled separately (original type only).
            return [];
        default:
            return [];
    }
};

export const normalizeDocumentType = (docType, fallbackType) => {
    return docType || fallbackType || '';
};

/**
 * Collect all transferable PDF URLs for a document (same rules as single transfer).
 * Deduplicates identical URLs within the same source document only.
 */
export const collectDocumentUrls = (editedItem) => {
    if (!editedItem) return [];

    const allDocumentUrls = [];

    const pushUrls = (value) => {
        if (!value) return;
        const list = Array.isArray(value) ? value : [value];
        list.forEach((url) => {
            if (url != null && url !== '') {
                allDocumentUrls.push(url);
            }
        });
    };

    pushUrls(editedItem.documentUrl);
    pushUrls(editedItem.receiptDocumentUrl);

    if (editedItem.docType === TRANSFER_DOC_TYPES.PaymentAcceptances) {
        const paymentArrays = ['checks', 'creditCards', 'cash', 'bankTransactions', 'offsets'];
        paymentArrays.forEach((key) => {
            if (Array.isArray(editedItem[key])) {
                editedItem[key].forEach((entry) => {
                    if (entry?.documentUrl && entry.documentUrl !== '') {
                        allDocumentUrls.push(entry.documentUrl);
                    }
                });
            }
        });
    }

    return [...new Set(allDocumentUrls)];
};

export const downloadUrlAsFile = async (url, fileName) => {
    const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
            Accept: 'application/pdf',
        },
        withCredentials: false,
        mode: 'cors',
    });
    const blob = response.data;
    return new File([blob], fileName, {
        type: blob.type || 'application/pdf',
    });
};

/**
 * Download all URLs for a document as File objects.
 * Uses document-{id}-{index}.pdf names for batch-friendly uniqueness.
 */
export const downloadDocumentFiles = async (document, { useUniqueNames = true } = {}) => {
    const urls = collectDocumentUrls(document);
    const files = [];

    for (let index = 0; index < urls.length; index++) {
        const url = urls[index];
        const fileName = useUniqueNames
            ? `document-${document.id}-${index + 1}.pdf`
            : 'downloaded_file.pdf';
        try {
            const file = await downloadUrlAsFile(url, fileName);
            files.push(file);
        } catch (error) {
            const err = new Error(`Failed to download file for document ${document.id}`);
            err.cause = error;
            err.documentId = document.id;
            err.url = url;
            throw err;
        }
    }

    return files;
};

export const buildTransferDto = (document, client, toType) => ({
    id: document.id,
    caseId: document.caseId,
    accountantId: client.accountantId,
    reportingMonthDate: document.reportingMonthDate || new Date(),
    registrationMonthDate: document.registrationMonthDate || new Date(),
    fromType: document.docType,
    toType,
    vat: document.vat ?? 100, // preserve explicit 0% VAT (0 || 100 would wrongly become 100)
    paymentAcceptanceType: document.paymentAcceptanceType || 'תקבול',
});

/**
 * Queue OCR upload for Incomes/Expenses (one source document's files).
 * Returns jobIds array or null on failure.
 */
export const enqueueIncomesExpensesOcr = async (dto, documents, token, t, toast) => {
    const formData = new FormData();
    formData.append('CaseId', dto.caseId);
    formData.append('AccountantId', dto.accountantId);
    formData.append('ReportingMonthDate', new Date(dto.reportingMonthDate).toISOString());
    formData.append('RegistrationMonthDate', new Date(dto.registrationMonthDate).toISOString());
    formData.append('DocumentFileType', dto.toType);
    formData.append('VAT', dto.vat);
    formData.append('GroupingMode', Constants.InvoiceGroupingMode.OnePerPdfPage);
    documents.forEach((document) => {
        formData.append('documents', document, document.name);
    });

    try {
        const response = await DocumentService.AsyncUploadOCRDocumentV2(dto.toType, formData, token);

        if (response.ok) {
            const result = await response.json();
            const jobIds = result.jobIds || [];

            if (jobIds.length === 0) {
                if (toast) {
                    toast.warning(t('documentsQueuedNoJobsReturned'));
                }
                return null;
            }

            if (toast) {
                toast.info(
                    t('documentsQueuedForProcessing', { count: result.queued ?? jobIds.length }),
                    { position: 'top-left' }
                );
            }
            return jobIds;
        }

        const errorText = await response.text();
        if (toast) {
            toast.error(t('documentUploadError', { errorText }));
        }
        return null;
    } catch (error) {
        if (toast) {
            toast.error(t('documentUploadError', { errorText: 'Error uploading document' }));
        }
        return null;
    }
};

export const deleteSourceDocument = async (fromType, id, token, t, toast) => {
    try {
        const deleteResponse = await DocumentService.DeleteDocument(fromType, id, token);
        if (deleteResponse.ok) {
            if (toast) {
                toast.success(t('SourceDocumentDeleted'));
            }
            return true;
        }
        const errorText = await deleteResponse.text();
        if (toast) {
            toast.error(t('ErrorDeletingSourceDocument', { errorText }));
        }
        return false;
    } catch (deleteError) {
        console.error('Error deleting source document:', deleteError);
        if (toast) {
            toast.error(t('ErrorDeletingSourceDocument', { errorText: deleteError.message }));
        }
        return false;
    }
};

/**
 * Process an async work queue with a small concurrency limit.
 */
export const mapWithConcurrency = async (items, concurrency, mapper) => {
    const results = new Array(items.length);
    let nextIndex = 0;

    const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (nextIndex < items.length) {
            const current = nextIndex;
            nextIndex += 1;
            results[current] = await mapper(items[current], current);
        }
    });

    await Promise.all(workers);
    return results;
};
